import * as React from 'react'
import { Loader2, LogOut } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useT } from '@/components/visitone/visitone-provider'
import { usePageTitle } from '@/hooks/use-page-title'
import { cn } from '@/lib/utils'

/*
 * EntryScreen: the centred card shown before the shell exists — the sign-in
 * landing, and the pre-tenant screens (choose a tenant, welcome, load error).
 * `landing` is the sign-in composition (centred content); `panel` is
 * start-aligned for screens that carry a list or feedback, `wide` for lists.
 */
export function EntryScreen({
  variant = 'panel',
  wide = false,
  children,
}: {
  variant?: 'landing' | 'panel'
  wide?: boolean
  children: React.ReactNode
}) {
  if (variant === 'landing') {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center bg-page px-6 py-16">
        <div className="flex w-full max-w-sm flex-col items-center gap-8 rounded-lg border border-line bg-surface p-8 shadow-e2">
          {children}
        </div>
      </main>
    )
  }

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center bg-page px-4 py-10 sm:px-6">
      <div
        className={cn(
          'flex w-full flex-col gap-6 rounded-lg border border-line bg-surface p-6 shadow-e2 sm:p-8',
          wide ? 'max-w-lg' : 'max-w-sm',
        )}
      >
        {children}
      </div>
    </main>
  )
}

/** An entry card's `h1`. Its text is also the document title. */
export function EntryTitle({ className, children }: { className?: string; children: string }) {
  usePageTitle(children)
  return <h1 className={cn('text-xl font-semibold text-fg-strong outline-none', className)}>{children}</h1>
}

/** Full-page progress message used while the session or tenant is being established; it is the page heading. */
export function FullPageStatus({ text }: { text: string }) {
  usePageTitle(text)
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-page px-6 text-center">
      <Loader2 aria-hidden="true" className="size-6 text-fg-muted motion-safe:animate-spin" />
      <div role="status">
        <h1 className="text-sm font-normal text-fg-muted">{text}</h1>
      </div>
    </main>
  )
}

/** Who is signed in, and Log out: the foot of every pre-shell entry card (tenant and access gates). */
export function SignedInFooter({ email, onLogout }: { email?: string; onLogout: () => void }) {
  const t = useT()
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line pt-4">
      {email ? (
        <p className="min-w-0 text-xs text-fg-muted">
          {t('core.auth.signedInAs')} <span className="break-all font-medium text-fg">{email}</span>
        </p>
      ) : (
        <span />
      )}
      <Button variant="ghost" size="sm" onClick={onLogout}>
        <LogOut aria-hidden="true" />
        {t('core.auth.logout')}
      </Button>
    </div>
  )
}
